import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '../../hooks/usePageTitle'
import {
  Search,
  ArrowLeft,
  UserCheck,
  UserX,
  X,
  CheckCircle,
  Shield,
  Building2,
  Plus,
  Loader2
} from 'lucide-react'
import { usersApi, medicalApi } from '../../lib/api'

const UserAccounts = () => {
  usePageTitle()
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [boundUserId, setBoundUserId] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const [confirmingStaff, setConfirmingStaff] = useState<any | null>(null)
  const [confirmType, setConfirmType] = useState<'freeze' | 'unfreeze' | 'logout' | 'activate'>('freeze')
  const [staffList, setStaffList] = useState<any[]>([])
  const [allUsers, setAllUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    window.scrollTo(0, 0)
    loadData()
  }, [])

  const loadData = () => {
    setLoading(true)
    const userAccount = JSON.parse(localStorage.getItem('userAccount') || '{}')
    const hospitalId = userAccount.hospital_id || ''
    Promise.all([
      medicalApi.list({ hospital_id: hospitalId }),
      usersApi.list({ status: 'active', page_size: 100 })
    ]).then(([staffRes, usersRes]) => {
      setStaffList(staffRes.items || staffRes || [])
      setAllUsers(usersRes.items || usersRes || [])
    }).catch(() => {
      setStaffList([])
      setAllUsers([])
    }).finally(() => setLoading(false))
  }

  const hospital = (() => {
    try {
      return JSON.parse(localStorage.getItem('userAccount') || '{}').hospital_name || '眉山市人民医院'
    } catch { return '眉山市人民医院' }
  })()

  const userSearchResults = allUsers.filter(u =>
    u.role === 'user' && u.status === 'active' &&
    ((u.name && u.name.includes(userSearch)) || (u.phone && u.phone.includes(userSearch)))
  )

  const filteredStaff = staffList.filter(s =>
    (s.name && s.name.includes(searchTerm)) || (s.phone && s.phone.includes(searchTerm))
  )

  const statusMap: Record<string, { label: string; color: string; bg: string }> = {
    active: { label: '在职', color: 'text-green-700', bg: 'bg-green-100' },
    frozen: { label: '已冻结', color: 'text-orange-700', bg: 'bg-orange-100' },
    inactive: { label: '已注销', color: 'text-gray-400', bg: 'bg-gray-100' },
  }

  const selectedUserInfo = allUsers.find(u => u.id === boundUserId)

  return (
    <div className="bg-gray-50 min-h-screen pb-6">
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
      )}
      {/* 顶部导航 */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-4 flex items-center space-x-4 sticky top-0 z-10">
        <Link to="/user/profile" className="p-2 hover:bg-white/20 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-white" />
        </Link>
        <h1 className="text-lg font-bold text-white">账号管理</h1>
        <div className="ml-auto flex items-center space-x-2">
          <Building2 className="w-4 h-4 text-green-200" />
          <span className="text-green-100 text-xs">{hospital}</span>
        </div>
      </div>

      {/* 搜索 */}
      <div className="px-4 py-3 bg-white">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="搜索姓名或手机号..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-gray-50"
          />
        </div>
      </div>

      {/* 添加按钮 */}
      <div className="px-4 pt-3">
        <button
          onClick={() => { setBoundUserId(''); setUserSearch(''); setShowModal(true) }}
          className="w-full flex items-center justify-center space-x-2 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium"
        >
          <Plus className="w-5 h-5" />
          <span>添加账号</span>
        </button>
      </div>

      {/* 账号列表 */}
      <div className="px-4 pt-3 space-y-3">
        {filteredStaff.map(staff => {
          const s = statusMap[staff.status] || statusMap.inactive
          return (
            <div key={staff.id} className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">{staff.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{staff.name}</p>
                    <p className="text-xs text-gray-400">{staff.phone}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${s.bg} ${s.color}`}>{s.label}</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                <span className="text-xs text-gray-400">{staff.hospital}</span>
                <div className="flex items-center space-x-1">
                  {staff.status === 'active' ? (
                    <>
                      <button
                        onClick={() => { setConfirmType('freeze'); setConfirmingStaff(staff) }}
                        className="px-3 py-1.5 text-xs text-orange-600 bg-orange-50 rounded-lg hover:bg-orange-100"
                      >冻结</button>
                      <button
                        onClick={() => { setConfirmType('logout'); setConfirmingStaff(staff) }}
                        className="px-3 py-1.5 text-xs text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                      >注销</button>
                    </>
                  ) : staff.status === 'frozen' ? (
                    <button
                      onClick={() => { setConfirmType('unfreeze'); setConfirmingStaff(staff) }}
                      className="px-3 py-1.5 text-xs text-green-600 bg-green-50 rounded-lg hover:bg-green-100"
                    >解冻</button>
                  ) : (
                    <button
                      onClick={() => { setConfirmType('activate'); setConfirmingStaff(staff) }}
                      className="px-3 py-1.5 text-xs text-green-600 bg-green-50 rounded-lg hover:bg-green-100"
                    >激活</button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* 添加账号弹窗 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
          <div className="bg-white rounded-t-3xl w-full max-w-md px-6 py-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">添加账号</h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* 机构信息 */}
            <div className="flex items-center space-x-3 p-3 bg-gray-50 border border-gray-100 rounded-xl mb-4">
              <Building2 className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-400">所属机构</p>
                <p className="text-sm font-medium text-gray-700">{hospital}</p>
              </div>
            </div>

            {/* 绑定用户 */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">绑定用户账号 <span className="text-red-500">*</span></label>
              {boundUserId && selectedUserInfo ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border-2 border-green-200 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-sm">{selectedUserInfo.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{selectedUserInfo.name}</p>
                      <p className="text-xs text-gray-400">{selectedUserInfo.phone}</p>
                    </div>
                  </div>
                  <button onClick={() => setBoundUserId('')} className="p-1.5 hover:bg-red-50 text-gray-400 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="搜索用户姓名或手机号..."
                    value={userSearch}
                    onChange={e => { setUserSearch(e.target.value); setUserDropdownOpen(e.target.value.length > 0) }}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-gray-50"
                  />
                  {userDropdownOpen && userSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {userSearchResults.map(u => (
                        <button
                          key={u.id}
                          onClick={() => { setBoundUserId(u.id); setUserSearch(''); setUserDropdownOpen(false) }}
                          className="w-full flex items-center space-x-3 p-3 hover:bg-green-50 text-left border-b border-gray-50 last:border-b-0"
                        >
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-green-700 font-bold text-xs">{u.name.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{u.name}</p>
                            <p className="text-xs text-gray-400">{u.phone}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 角色固定提示 */}
            <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-100 rounded-xl mb-5">
              <Shield className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm font-medium text-blue-700">角色：医护人员</p>
                <p className="text-xs text-blue-400">绑定后账号角色为普通医护人员，不可更改</p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 font-medium">取消</button>
              <button
                onClick={() => {
                  if (!boundUserId || !selectedUserInfo) return
                  medicalApi.create({
                    phone: selectedUserInfo.phone,
                    name: selectedUserInfo.name,
                    id_card: selectedUserInfo.id_card || selectedUserInfo.idCard || '',
                    hospital_id: JSON.parse(localStorage.getItem('userAccount') || '{}').hospital_id || '',
                    medical_role: 'doctor'
                  }).then(() => {
                    setShowModal(false)
                    loadData()
                  }).catch(() => alert('添加失败'))
                }}
                disabled={!boundUserId}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium disabled:opacity-50"
              >
                确认添加
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 确认弹窗 */}
      {confirmingStaff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 p-6 text-center">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${confirmType === 'freeze' || confirmType === 'logout' ? 'bg-red-100' : 'bg-green-100'}`}>
              {confirmType === 'freeze' || confirmType === 'logout' ? <UserX className="w-7 h-7 text-red-600" /> : <UserCheck className="w-7 h-7 text-green-600" />}
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              {confirmType === 'freeze' ? '确认冻结' : confirmType === 'unfreeze' ? '确认解冻' : confirmType === 'logout' ? '确认注销' : '确认激活'}
            </h3>
            <p className="text-gray-500 text-sm mb-6">
              确定要{confirmType === 'freeze' ? '冻结' : confirmType === 'unfreeze' ? '解冻' : confirmType === 'logout' ? '注销' : '激活'}账号 <strong>{confirmingStaff.name}</strong> 吗？
            </p>
            <div className="flex space-x-3">
              <button onClick={() => setConfirmingStaff(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 font-medium">取消</button>
              <button
                onClick={() => {
                  if (confirmType === 'freeze') {
                    usersApi.freeze(confirmingStaff.id)
                      .then(() => loadData())
                      .catch(() => alert('操作失败'))
                  } else if (confirmType === 'unfreeze') {
                    usersApi.unfreeze(confirmingStaff.id)
                      .then(() => loadData())
                      .catch(() => alert('操作失败'))
                  } else if (confirmType === 'logout' || confirmType === 'activate') {
                    medicalApi.delete(confirmingStaff.id)
                      .then(() => loadData())
                      .catch(() => alert('操作失败'))
                  }
                  setConfirmingStaff(null)
                }}
                className={`flex-1 py-2.5 rounded-xl font-medium text-white ${confirmType === 'freeze' || confirmType === 'logout' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                确认
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserAccounts